/**
 * Debt Optimization Utilities
 * Provides calculations for debt payoff strategies (Avalanche vs Snowball)
 */

/**
 * Calculate debt payoff using the Avalanche method (highest interest rate first)
 * @param {Array} liabilities - Array of liability objects
 * @param {number} extraPayment - Additional monthly payment beyond minimums
 * @returns {Object} Payoff strategy details
 */
export const calculateAvalancheStrategy = (liabilities, extraPayment = 0) => {
  if (!liabilities || liabilities.length === 0) {
    return null;
  }

  // Sort by interest rate (highest first)
  const sorted = [...liabilities].sort((a, b) => b.interestRate - a.interestRate);

  let totalMonths = 0;
  let totalInterestPaid = 0;
  const payoffOrder = [];

  // Calculate payoff timeline
  sorted.forEach((debt, index) => {
    const monthlyPayment = parseFloat(debt.monthlyPayment) || 0;
    const balance = parseFloat(debt.currentBalance) || 0;
    const rate = parseFloat(debt.interestRate) || 0;
    const monthlyRate = rate / 100 / 12;

    // Add extra payment to first unpaid debt
    const payment = index === 0 ? monthlyPayment + extraPayment : monthlyPayment;

    // Calculate months to payoff using amortization formula
    let months = 0;
    let interestPaid = 0;

    if (monthlyRate > 0 && payment > balance * monthlyRate) {
      months = Math.ceil(
        Math.log(payment / (payment - balance * monthlyRate)) /
        Math.log(1 + monthlyRate)
      );
      interestPaid = (payment * months) - balance;
    } else if (monthlyRate === 0 && payment > 0) {
      months = Math.ceil(balance / payment);
      interestPaid = 0;
    } else {
      // Payment doesn't cover interest - debt growing
      months = 999; // Flag as problematic
      interestPaid = balance * 0.5; // Estimate
    }

    totalMonths = Math.max(totalMonths, months);
    totalInterestPaid += interestPaid;

    payoffOrder.push({
      name: debt.name,
      type: debt.type,
      balance: balance,
      interestRate: rate,
      monthlyPayment: payment,
      monthsToPayoff: months,
      interestPaid: interestPaid,
      priority: index + 1
    });
  });

  return {
    strategy: 'avalanche',
    payoffOrder,
    totalMonths,
    totalInterestPaid,
    description: 'Highest interest rate first - mathematically optimal'
  };
};

/**
 * Calculate debt payoff using the Snowball method (smallest balance first)
 * @param {Array} liabilities - Array of liability objects
 * @param {number} extraPayment - Additional monthly payment beyond minimums
 * @returns {Object} Payoff strategy details
 */
export const calculateSnowballStrategy = (liabilities, extraPayment = 0) => {
  if (!liabilities || liabilities.length === 0) {
    return null;
  }

  // Sort by balance (smallest first)
  const sorted = [...liabilities].sort((a, b) =>
    parseFloat(a.currentBalance) - parseFloat(b.currentBalance)
  );

  let totalMonths = 0;
  let totalInterestPaid = 0;
  const payoffOrder = [];

  // Calculate payoff timeline
  sorted.forEach((debt, index) => {
    const monthlyPayment = parseFloat(debt.monthlyPayment) || 0;
    const balance = parseFloat(debt.currentBalance) || 0;
    const rate = parseFloat(debt.interestRate) || 0;
    const monthlyRate = rate / 100 / 12;

    // Add extra payment to first unpaid debt
    const payment = index === 0 ? monthlyPayment + extraPayment : monthlyPayment;

    // Calculate months to payoff
    let months = 0;
    let interestPaid = 0;

    if (monthlyRate > 0 && payment > balance * monthlyRate) {
      months = Math.ceil(
        Math.log(payment / (payment - balance * monthlyRate)) /
        Math.log(1 + monthlyRate)
      );
      interestPaid = (payment * months) - balance;
    } else if (monthlyRate === 0 && payment > 0) {
      months = Math.ceil(balance / payment);
      interestPaid = 0;
    } else {
      months = 999;
      interestPaid = balance * 0.5;
    }

    totalMonths = Math.max(totalMonths, months);
    totalInterestPaid += interestPaid;

    payoffOrder.push({
      name: debt.name,
      type: debt.type,
      balance: balance,
      interestRate: rate,
      monthlyPayment: payment,
      monthsToPayoff: months,
      interestPaid: interestPaid,
      priority: index + 1
    });
  });

  return {
    strategy: 'snowball',
    payoffOrder,
    totalMonths,
    totalInterestPaid,
    description: 'Smallest balance first - psychological momentum'
  };
};

/**
 * Compare Avalanche vs Snowball strategies
 * @param {Array} liabilities - Array of liability objects
 * @param {number} extraPayment - Additional monthly payment beyond minimums
 * @returns {Object} Comparison of both strategies
 */
export const compareStrategies = (liabilities, extraPayment = 0) => {
  const avalanche = calculateAvalancheStrategy(liabilities, extraPayment);
  const snowball = calculateSnowballStrategy(liabilities, extraPayment);

  if (!avalanche || !snowball) {
    return null;
  }

  return {
    avalanche,
    snowball,
    interestSavings: snowball.totalInterestPaid - avalanche.totalInterestPaid,
    timeDifference: snowball.totalMonths - avalanche.totalMonths,
    recommendation: determineRecommendation(liabilities, avalanche, snowball)
  };
};

/**
 * Determine which strategy to recommend
 * @param {Array} liabilities - Array of liability objects
 * @param {Object} avalanche - Avalanche strategy results
 * @param {Object} snowball - Snowball strategy results
 * @returns {string} Recommendation
 */
const determineRecommendation = (liabilities, avalanche, snowball) => {
  // Check for high-interest debt (>15%)
  const hasHighInterestDebt = liabilities.some(debt =>
    parseFloat(debt.interestRate) > 15
  );

  const interestSavings = snowball.totalInterestPaid - avalanche.totalInterestPaid;

  if (hasHighInterestDebt && interestSavings > 1000) {
    return 'avalanche - High interest debt requires mathematical approach';
  } else if (interestSavings > 2000) {
    return 'avalanche - Significant interest savings';
  } else if (liabilities.length <= 2) {
    return 'avalanche - Few debts, optimization matters';
  } else if (interestSavings < 500) {
    return 'snowball - Minimal difference, psychological wins preferred';
  } else {
    return 'hybrid - Tackle high-interest debts first, then snowball';
  }
};

/**
 * Categorize debt by interest rate
 * @param {Array} liabilities - Array of liability objects
 * @returns {Object} Categorized debts
 */
export const categorizeDebtByInterest = (liabilities) => {
  if (!liabilities || liabilities.length === 0) {
    return { critical: [], high: [], medium: [], low: [] };
  }

  return {
    critical: liabilities.filter(d => parseFloat(d.interestRate) >= 20),
    high: liabilities.filter(d => parseFloat(d.interestRate) >= 15 && parseFloat(d.interestRate) < 20),
    medium: liabilities.filter(d => parseFloat(d.interestRate) >= 7 && parseFloat(d.interestRate) < 15),
    low: liabilities.filter(d => parseFloat(d.interestRate) < 7)
  };
};

/**
 * Calculate total monthly debt payments
 * @param {Array} liabilities - Array of liability objects
 * @returns {number} Total monthly payment
 */
export const calculateTotalMonthlyPayment = (liabilities) => {
  if (!liabilities || liabilities.length === 0) {
    return 0;
  }

  return liabilities.reduce((total, debt) => {
    return total + (parseFloat(debt.monthlyPayment) || 0);
  }, 0);
};

/**
 * Get debt priority recommendations
 * @param {Array} liabilities - Array of liability objects
 * @returns {Array} Sorted priorities with reasoning
 */
export const getDebtPriorities = (liabilities) => {
  if (!liabilities || liabilities.length === 0) {
    return [];
  }

  return liabilities.map(debt => {
    const rate = parseFloat(debt.interestRate) || 0;
    let priority = 'low';
    let reasoning = '';

    if (rate >= 20) {
      priority = 'critical';
      reasoning = 'Extremely high interest - immediate action required';
    } else if (rate >= 15) {
      priority = 'high';
      reasoning = 'High interest - prioritize payoff over investing';
    } else if (rate >= 7) {
      priority = 'medium';
      reasoning = 'Moderate interest - balance with investing goals';
    } else {
      priority = 'low';
      reasoning = 'Low interest - minimum payments while investing';
    }

    return {
      ...debt,
      priority,
      reasoning,
      urgency: rate >= 15 ? 'urgent' : 'normal'
    };
  }).sort((a, b) => parseFloat(b.interestRate) - parseFloat(a.interestRate));
};
